// Insurance section. Sub-routes:
//   /insurance            redirect → /insurance/policies
//   /insurance/policies   PolicyList
//   /insurance/renewals   RenewalCalendar
//   /insurance/quotes     placeholder
//
// The section owns:
//   • InsuranceDashboard widget (always visible above the active sub-route)
//   • Upload Policies button
//   • Upload modal + extraction review queue + edit-policy modal
//   • Selected-state filter (clicking a state tile in the dashboard
//     navigates to /policies and applies the filter)
//
// Cross-route data flows via InsuranceSectionContext so PolicyList can
// trigger the section-level edit modal and read/write the state filter.

import { useState, createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionLayout from '../../components/common/SectionLayout';
import InsuranceDashboard from '../../components/Insurance/InsuranceDashboard';
import UploadPolicyModal from '../../components/Insurance/UploadPolicyModal';
import ExtractionReviewForm from '../../components/Insurance/ExtractionReviewForm';

const InsuranceSectionContext = createContext(null);

export function useInsuranceSection() {
  const ctx = useContext(InsuranceSectionContext);
  if (!ctx) throw new Error('useInsuranceSection must be used inside InsuranceSection');
  return ctx;
}

const ITEMS = [
  { to: '/insurance/policies', label: 'Policies' },
  { to: '/insurance/renewals', label: 'Renewals' },
  { to: '/insurance/quotes', label: 'Quotes' },
];

export default function InsuranceSection() {
  const navigate = useNavigate();
  const [showUpload, setShowUpload] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [originalQueueSize, setOriginalQueueSize] = useState(0);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [selectedState, setSelectedState] = useState('');

  const handleUploadDone = useCallback((items) => {
    setShowUpload(false);
    setReviewQueue(items);
    setReviewedCount(0);
    setOriginalQueueSize(items.length);
  }, []);

  const handleReviewDone = useCallback(() => {
    setReviewQueue(prev => prev.slice(1));
    setReviewedCount(c => c + 1);
    setRefreshKey(k => k + 1);
  }, []);

  const handleReviewSkip = useCallback(() => {
    setReviewQueue(prev => prev.slice(1));
    setReviewedCount(c => c + 1);
  }, []);

  const handleEditPolicy = useCallback((policy) => setEditingPolicy(policy), []);
  const handleEditDone = useCallback(() => {
    setEditingPolicy(null);
    setRefreshKey(k => k + 1);
  }, []);

  // Clicking a state tile in the dashboard switches to Policies view
  // and applies the filter; clicking the same state again clears it.
  const onSelectState = useCallback((st) => {
    setSelectedState(prev => prev === st ? '' : st);
    navigate('/insurance/policies');
  }, [navigate]);

  const ctxValue = {
    refreshKey,
    bumpRefresh: () => setRefreshKey(k => k + 1),
    selectedState,
    setSelectedState,
    openEditPolicy: handleEditPolicy,
  };

  const currentReviewItem = reviewQueue[0];

  const uploadButton = (
    <button
      onClick={() => setShowUpload(true)}
      className="px-3 py-1.5 text-[11px] font-bold text-white bg-bullogic-blue rounded-[3px] hover:bg-bullogic-blue/90"
    >
      + Upload Policies
    </button>
  );

  return (
    <InsuranceSectionContext.Provider value={ctxValue}>
      {/* SectionLayout renders sub-nav, then `children` (the dashboard
          widget), then the active sub-route via its internal <Outlet/>. */}
      <SectionLayout title="Insurance" items={ITEMS} actions={uploadButton}>
        <InsuranceDashboard
          refreshKey={refreshKey}
          selectedState={selectedState}
          onSelectState={onSelectState}
        />
      </SectionLayout>

      {/* Section-level modals (position: fixed, visible across sub-routes) */}
      {showUpload && (
        <UploadPolicyModal
          onClose={() => setShowUpload(false)}
          onAllExtracted={handleUploadDone}
        />
      )}

      {currentReviewItem && (
        <ExtractionReviewForm
          extractedData={currentReviewItem.extracted}
          documentId={currentReviewItem.documentId}
          fileName={currentReviewItem.fileName}
          queuePosition={originalQueueSize > 1 ? { current: reviewedCount + 1, total: originalQueueSize } : null}
          onSave={handleReviewDone}
          onCancel={handleReviewSkip}
        />
      )}

      {editingPolicy && (
        <ExtractionReviewForm
          existingPolicy={editingPolicy}
          extractedData={editingPolicy.extracted_data}
          documentId={editingPolicy.source_document_id}
          onSave={handleEditDone}
          onCancel={() => setEditingPolicy(null)}
        />
      )}
    </InsuranceSectionContext.Provider>
  );
}
